import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import Notification from "../components/common/Notification";
import styles from "./AdminPage.module.css";

function AdminMenuEditPage() {
  const { itemId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isNew = location.pathname.includes("/new");

  const [item, setItem] = useState({
    id: "",
    name: "",
    category: "",
    description: "",
    price: "",
  });

  const [categories, setCategories] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  const showToast = (message, type = "success") => {
    setNotification({ message, type });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const loadPageData = async () => {
      setIsLoading(true);
      try {
        const catRes = await fetch("/api/categories?type=Menu");
        if (!catRes.ok) throw new Error("Failed to load categories");
        const catData = await catRes.json();
        setCategories(catData);

        let defaultCatId = catData.length > 0 ? catData[0]._id : "";

        if (!isNew) {
          if (!itemId) throw new Error("No Item ID provided");

          const prodRes = await fetch(`/api/cms/products/${itemId}`);
          if (!prodRes.ok) throw new Error("Item not found");
          const data = await prodRes.json();

          setItem({
            id: data.productID || "",
            name: data.name || "",
            category: data.category ? data.category._id : defaultCatId,
            description: data.description || "",
            price: data.price ? String(data.price) : "", // Ensure it loads as a string
          });
          setExistingImageUrl(data.imageURL);
        } else {
          setItem({
            id: "",
            name: "",
            category: defaultCatId,
            description: "",
            price: "",
          });
          setExistingImageUrl("");
          setImageFile(null);
        }
      } catch (err) {
        showToast(err.message, "error");
      } finally {
        setIsLoading(false);
      }
    };
    loadPageData();
  }, [itemId, isNew, navigate]);

  const handleCategoryChange = (e) => {
    const selectedId = e.target.value;
    setItem((prev) => ({ ...prev, category: selectedId }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData();
    formData.append("productID", item.id);
    formData.append("name", item.name);
    formData.append("category", item.category);
    formData.append("description", item.description);
    // CHANGE: Send price as a raw string to allow "/"
    formData.append("price", item.price);
    formData.append(
      "sizePrices",
      JSON.stringify({ regular: 0, hbs: 0, large: 0 })
    );

    if (imageFile) {
      formData.append("image", imageFile);
    }

    const url = isNew ? "/api/cms/products" : `/api/cms/products/${itemId}`;
    const method = isNew ? "POST" : "PUT";
    const adminKey = localStorage.getItem("adminKey");

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          "x-admin-key": adminKey,
        },
        body: formData,
      });

      const data = await response.json();
      if (response.ok) {
        showToast("Item saved successfully!", "success");
        setTimeout(() => {
          navigate("/admin/menu");
        }, 1500);
      } else {
        showToast(`Failed: ${data.message || "Not authorized"}`, "error");
      }
    } catch (error) {
      showToast("Network Error", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.adminPage}>
      <Navbar />
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <div className={styles.adminHero}>
        <h1>{isNew ? "Add Menu Item" : "Edit Menu Item"}</h1>
      </div>
      <div className={styles.adminContentContainer}>
        {isLoading ? (
          <p>Loading...</p>
        ) : (
          <form className={styles.adminEditForm} onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label>Unique ID (SKU) *</label>
              <input
                type="text"
                name="id"
                value={item.id}
                onChange={handleInputChange}
                required
                disabled={!isNew}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Item Name *</label>
              <input
                type="text"
                name="name"
                value={item.name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>Category *</label>
              <select
                name="category"
                value={item.category}
                onChange={handleCategoryChange}
                disabled={categories.length === 0}
              >
                {categories.length === 0 && (
                  <option>Loading categories...</option>
                )}
                {categories.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Description</label>
              <textarea
                name="description"
                value={item.description}
                onChange={handleInputChange}
                rows="3"
              ></textarea>
            </div>

            <div className={styles.formGroup}>
              <label>Price (ETB) </label>
              {/* CHANGE: Input type changed to 'text' to allow '/' */}
              <input
                type="text"
                name="price"
                value={item.price}
                onChange={handleInputChange}
                placeholder="e.g. 120/240"
              />
            </div>

            <div className={styles.formGroup}>
              <label>Item Image (Optional)</label>
              <input type="file" onChange={handleFileChange} accept="image/*" />
              {!isNew && existingImageUrl && (
                <div style={{ marginTop: 10 }}>
                  <img
                    src={`${existingImageUrl}`}
                    alt="Current"
                    style={{
                      width: 100,
                      borderRadius: 4,
                      border: "1px solid #333",
                    }}
                  />
                </div>
              )}
            </div>

            <div className={styles.formActions}>
              <Link
                to="/admin/menu"
                className={`${styles.adminBtn} ${styles.danger}`}
              >
                Cancel
              </Link>
              <button
                type="submit"
                className={`${styles.adminBtn} ${styles.primary}`}
                disabled={isLoading}
              >
                Save
              </button>
            </div>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default AdminMenuEditPage;
